import type { LangMap } from "./index";

export const voice: LangMap = {
  it: {
    tab: "Voce",
    modes: {
      explain: { label: "Spiegami", hint: "Spiega l'argomento passo passo, con esempi concreti, e verifica che abbia capito." },
      quiz: { label: "Interrogami", hint: "Fai da professore: interroga con una domanda alla volta, valuta la risposta e correggi con gentilezza." },
      together: { label: "Studiamo insieme", hint: "Studia insieme allo studente: alternate lettura, domande e riassunti brevi." },
    },
    voiceOption: "Voce {{name}}",
    subtitles: "Sottotitoli",
    transcript: "Trascrizione",
    empty: "Tocca il microfono e parla: il tutor ti risponde a voce. Puoi interromperlo in qualsiasi momento ricominciando a parlare.",
    thinking: "Sto pensando…",
    micStop: "Ferma e invia",
    micStart: "Parla con il tutor",
    pause: "Metti in pausa",
    resume: "Riprendi",
    end: "Termina conversazione",
    saveClose: "Salva e chiudi sessione",
    sessionTitle: "Sessione vocale",
    errors: {
      voice: "Voce non disponibile",
      mic: "Microfono non disponibile",
      generic: "Si è verificato un errore",
      expired: "Sessione scaduta",
      noReply: "Nessuna risposta",
      denied: "Permesso microfono negato: attivalo dalle impostazioni del browser",
      blocked: "Il microfono è bloccato nell'anteprima: apri l'app in una nuova scheda per usarlo",
      notFound: "Nessun microfono rilevato",
      insecure: "Il microfono richiede una connessione sicura (https)",
      unsupported: "Questo browser non supporta il microfono",
    },
    saved: "Sessione salvata",
    status: { ready: "Pronto", listening: "Ti ascolto…", thinking: "Sto pensando…", speaking: "Il tutor parla", paused: "In pausa", unavailable: "Microfono non disponibile", checking: "Controllo microfono…" },
    fallback: { title: "Puoi comunque scrivere al tutor", retry: "Riprova microfono", placeholder: "Scrivi qui la tua domanda…", send: "Invia", hint: "Il tutor risponde a voce anche se scrivi." },
  },
  en: {
    tab: "Voice",
    modes: {
      explain: { label: "Explain it", hint: "Explain the topic step by step with concrete examples, and check I understood." },
      quiz: { label: "Quiz me", hint: "Act like a teacher: ask one question at a time, judge the answer and correct me kindly." },
      together: { label: "Study together", hint: "Study alongside the student: alternate reading, questions and short summaries." },
    },
    voiceOption: "Voice {{name}}",
    subtitles: "Subtitles",
    transcript: "Transcript",
    empty: "Tap the microphone and talk: the tutor answers out loud. You can interrupt any time by speaking again.",
    thinking: "Thinking…",
    micStop: "Stop and send",
    micStart: "Talk to the tutor",
    pause: "Pause",
    resume: "Resume",
    end: "End conversation",
    saveClose: "Save and close session",
    sessionTitle: "Voice session",
    errors: {
      voice: "Voice not available",
      mic: "Microphone not available",
      generic: "Something went wrong",
      expired: "Session expired",
      noReply: "No answer",
      denied: "Microphone permission denied: enable it in your browser settings",
      blocked: "The microphone is blocked in the preview: open the app in a new tab to use it",
      notFound: "No microphone detected",
      insecure: "The microphone requires a secure (https) connection",
      unsupported: "This browser does not support microphone input",
    },
    saved: "Session saved",
    status: { ready: "Ready", listening: "Listening…", thinking: "Thinking…", speaking: "Tutor is speaking", paused: "Paused", unavailable: "Microphone unavailable", checking: "Checking microphone…" },
    fallback: { title: "You can still type to the tutor", retry: "Retry microphone", placeholder: "Type your question here…", send: "Send", hint: "The tutor answers out loud even when you type." },
  },
  es: {
    tab: "Voz",
    modes: {
      explain: { label: "Explícamelo", hint: "Explica el tema paso a paso, con ejemplos concretos, y comprueba que lo he entendido." },
      quiz: { label: "Pregúntame", hint: "Haz de profesor: pregunta de una en una, valora la respuesta y corrige con amabilidad." },
      together: { label: "Estudiemos juntos", hint: "Estudia junto al estudiante: alternad lectura, preguntas y resúmenes breves." },
    },
    voiceOption: "Voz {{name}}",
    subtitles: "Subtítulos",
    transcript: "Transcripción",
    empty: "Toca el micrófono y habla: el tutor te responde en voz alta. Puedes interrumpirlo cuando quieras volviendo a hablar.",
    thinking: "Pensando…",
    micStop: "Parar y enviar",
    micStart: "Habla con el tutor",
    pause: "Pausar",
    resume: "Reanudar",
    end: "Terminar conversación",
    saveClose: "Guardar y cerrar sesión",
    sessionTitle: "Sesión de voz",
    errors: {
      voice: "Voz no disponible",
      mic: "Micrófono no disponible",
      generic: "Ha ocurrido un error",
      expired: "Sesión caducada",
      noReply: "Sin respuesta",
      denied: "Permiso de micrófono denegado: actívalo en la configuración del navegador",
      blocked: "El micrófono está bloqueado en la vista previa: abre la app en una pestaña nueva",
      notFound: "No se ha detectado ningún micrófono",
      insecure: "El micrófono requiere una conexión segura (https)",
      unsupported: "Este navegador no admite el micrófono",
    },
    saved: "Sesión guardada",
    status: { ready: "Listo", listening: "Te escucho…", thinking: "Pensando…", speaking: "El tutor habla", paused: "En pausa", unavailable: "Micrófono no disponible", checking: "Comprobando micrófono…" },
    fallback: { title: "También puedes escribir al tutor", retry: "Reintentar micrófono", placeholder: "Escribe aquí tu pregunta…", send: "Enviar", hint: "El tutor responde en voz alta aunque escribas." },
  },
  fr: {
    tab: "Voix",
    modes: {
      explain: { label: "Explique-moi", hint: "Explique le sujet pas à pas, avec des exemples concrets, et vérifie que j'ai compris." },
      quiz: { label: "Interroge-moi", hint: "Joue le prof : pose une question à la fois, évalue la réponse et corrige avec bienveillance." },
      together: { label: "Étudions ensemble", hint: "Étudie avec l'élève : alternez lecture, questions et résumés courts." },
    },
    voiceOption: "Voix {{name}}",
    subtitles: "Sous-titres",
    transcript: "Transcription",
    empty: "Touche le micro et parle : le tuteur te répond à voix haute. Tu peux l'interrompre à tout moment en reparlant.",
    thinking: "Je réfléchis…",
    micStop: "Arrêter et envoyer",
    micStart: "Parler au tuteur",
    pause: "Mettre en pause",
    resume: "Reprendre",
    end: "Terminer la conversation",
    saveClose: "Enregistrer et fermer la session",
    sessionTitle: "Session vocale",
    errors: {
      voice: "Voix indisponible",
      mic: "Micro indisponible",
      generic: "Une erreur est survenue",
      expired: "Session expirée",
      noReply: "Aucune réponse",
      denied: "Autorisation du micro refusée : active-la dans les réglages du navigateur",
      blocked: "Le micro est bloqué dans l'aperçu : ouvre l'app dans un nouvel onglet",
      notFound: "Aucun micro détecté",
      insecure: "Le micro nécessite une connexion sécurisée (https)",
      unsupported: "Ce navigateur ne prend pas en charge le micro",
    },
    saved: "Session enregistrée",
    status: { ready: "Prêt", listening: "Je t'écoute…", thinking: "Je réfléchis…", speaking: "Le tuteur parle", paused: "En pause", unavailable: "Micro indisponible", checking: "Vérification du micro…" },
    fallback: { title: "Tu peux aussi écrire au tuteur", retry: "Réessayer le micro", placeholder: "Écris ta question ici…", send: "Envoyer", hint: "Le tuteur répond à voix haute même si tu écris." },
  },
  de: {
    tab: "Stimme",
    modes: {
      explain: { label: "Erkläre es mir", hint: "Erkläre das Thema Schritt für Schritt mit konkreten Beispielen und prüfe, ob ich es verstanden habe." },
      quiz: { label: "Prüfe mich", hint: "Sei die Lehrkraft: stelle eine Frage nach der anderen, bewerte die Antwort und korrigiere freundlich." },
      together: { label: "Gemeinsam lernen", hint: "Lerne mit der Schülerin/dem Schüler: wechselt zwischen Lesen, Fragen und kurzen Zusammenfassungen." },
    },
    voiceOption: "Stimme {{name}}",
    subtitles: "Untertitel",
    transcript: "Transkript",
    empty: "Tippe auf das Mikrofon und sprich: der Tutor antwortet laut. Du kannst ihn jederzeit unterbrechen, indem du wieder sprichst.",
    thinking: "Ich denke nach…",
    micStop: "Stoppen und senden",
    micStart: "Mit dem Tutor sprechen",
    pause: "Pausieren",
    resume: "Fortsetzen",
    end: "Gespräch beenden",
    saveClose: "Sitzung speichern und schließen",
    sessionTitle: "Sprachsitzung",
    errors: {
      voice: "Stimme nicht verfügbar",
      mic: "Mikrofon nicht verfügbar",
      generic: "Etwas ist schiefgelaufen",
      expired: "Sitzung abgelaufen",
      noReply: "Keine Antwort",
      denied: "Mikrofonzugriff verweigert: erlaube ihn in den Browsereinstellungen",
      blocked: "Das Mikrofon ist in der Vorschau blockiert: öffne die App in einem neuen Tab",
      notFound: "Kein Mikrofon gefunden",
      insecure: "Das Mikrofon benötigt eine sichere (https) Verbindung",
      unsupported: "Dieser Browser unterstützt kein Mikrofon",
    },
    saved: "Sitzung gespeichert",
    status: { ready: "Bereit", listening: "Ich höre zu…", thinking: "Denke nach…", speaking: "Tutor spricht", paused: "Pausiert", unavailable: "Mikrofon nicht verfügbar", checking: "Mikrofon wird geprüft…" },
    fallback: { title: "Du kannst dem Tutor auch schreiben", retry: "Mikrofon erneut versuchen", placeholder: "Schreib hier deine Frage…", send: "Senden", hint: "Der Tutor antwortet auch beim Tippen laut." },
  },
  pt: {
    tab: "Voz",
    modes: {
      explain: { label: "Explica-me", hint: "Explica o tema passo a passo, com exemplos concretos, e confirma que percebi." },
      quiz: { label: "Faz-me perguntas", hint: "Age como professor: pergunta uma de cada vez, avalia a resposta e corrige com gentileza." },
      together: { label: "Estudar juntos", hint: "Estuda com o aluno: alternem leitura, perguntas e resumos curtos." },
    },
    voiceOption: "Voz {{name}}",
    subtitles: "Legendas",
    transcript: "Transcrição",
    empty: "Toca no microfone e fala: o tutor responde em voz alta. Podes interrompê-lo a qualquer momento voltando a falar.",
    thinking: "A pensar…",
    micStop: "Parar e enviar",
    micStart: "Falar com o tutor",
    pause: "Pausar",
    resume: "Retomar",
    end: "Terminar conversa",
    saveClose: "Guardar e fechar sessão",
    sessionTitle: "Sessão de voz",
    errors: {
      voice: "Voz indisponível",
      mic: "Microfone indisponível",
      generic: "Ocorreu um erro",
      expired: "Sessão expirada",
      noReply: "Sem resposta",
      denied: "Permissão do microfone negada: ativa-a nas definições do navegador",
      blocked: "O microfone está bloqueado na pré-visualização: abre a app num novo separador",
      notFound: "Nenhum microfone detetado",
      insecure: "O microfone precisa de uma ligação segura (https)",
      unsupported: "Este navegador não suporta microfone",
    },
    saved: "Sessão guardada",
    status: { ready: "Pronto", listening: "A ouvir…", thinking: "A pensar…", speaking: "O tutor está a falar", paused: "Em pausa", unavailable: "Microfone indisponível", checking: "A verificar o microfone…" },
    fallback: { title: "Também podes escrever ao tutor", retry: "Tentar microfone novamente", placeholder: "Escreve aqui a tua pergunta…", send: "Enviar", hint: "O tutor responde em voz alta mesmo quando escreves." },
  },
};
